import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CompanionGroupSettings, CompanionInviteResult, Member } from '@cloudsign/shared';
import QRCode from 'qrcode';
import { useParams } from 'react-router';
import { Button, Card, CheckboxField, ConfirmButton, EmptyState, PageHeader, Spinner, TableWrap, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { StatusPill } from '../../components/phase2';
import type { SettingsCardProps } from '../../components/SettingsCards';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useScopedSettings } from '../sites/api';
import { DeviceList, devicesKey, useModuleOn } from './api';

type Perms = CompanionGroupSettings['permissions'];
const PERMISSION_KEYS: (keyof Perms)[] = ['mobileSignIn', 'locationSignIn', 'preRegister', 'ownHistory', 'todayView', 'evacuationList'];

/** Member page tab "CloudSign app": devices, invite by email, show a QR. Mounted by the members drawer (W15) or the page below. */
export function CompanionMemberTab({ memberId }: { memberId: string }) {
  const on = useModuleOn('companion');
  const canManage = useCan('groups.manage_members');
  const canView = useCan('groups.view_members');
  const qc = useQueryClient();
  const toast = useToast();
  const [qr, setQr] = useState<string | null>(null);
  const member = useQuery({ queryKey: ['members', 'one', memberId], queryFn: () => api.get(`/members/${memberId}`, { schema: Member }) });
  const devices = useQuery({
    queryKey: devicesKey(memberId),
    enabled: on && canView,
    queryFn: () => api.get('/companion/devices', { query: { memberId }, schema: DeviceList }),
  });
  const invite = useMutation({
    mutationFn: (send: 'email' | 'none') => api.post('/companion/invites', { body: { memberIds: [memberId], send }, schema: CompanionInviteResult }),
    onSuccess: async (r, send) => {
      if (send === 'none' && r.link) setQr(await QRCode.toDataURL(r.link, { margin: 1, width: 240 }));
      else toast(t('peo.member.inviteSent'));
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/companion/devices/${id}`),
    onSuccess: () => {
      toast(t('peo.member.revoked'));
      void qc.invalidateQueries({ queryKey: devicesKey(memberId) });
    },
  });
  if (!on) return null;
  const email = member.data?.email ?? null;
  const live = (devices.data ?? []).filter((d) => d.revokedAt === null);
  return (
    <div className="space-y-4">
      <Card title={t('peo.member.title')} description={t('peo.member.description')}>
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" disabled={!canManage || !email || invite.isPending} onClick={() => invite.mutate('email')}>
            {t('peo.member.invite')}
          </Button>
          <Button disabled={!canManage || invite.isPending} onClick={() => invite.mutate('none')}>
            {t('peo.member.showQr')}
          </Button>
        </div>
        {!email && member.data ? <p className="mt-2 text-sm text-slate-600">{t('peo.member.noEmail')}</p> : null}
        <ErrorLine error={invite.error} />
        {qr ? (
          <figure className="mt-4">
            <img src={qr} alt={t('peo.member.qrAlt')} width={240} height={240} className="rounded-md border border-slate-200" />
            <figcaption className="mt-2 text-sm text-slate-600">{t('peo.member.qrHelp')}</figcaption>
          </figure>
        ) : null}
      </Card>
      {canView ? (
        <Card title={t('peo.devices.title')}>
          {devices.isError ? (
            <ErrorPanel error={devices.error} onRetry={() => void devices.refetch()} />
          ) : !devices.data ? (
            <Spinner />
          ) : live.length === 0 ? (
            <EmptyState message={t('peo.devices.empty')} />
          ) : (
            <TableWrap caption={t('peo.devices.title')}>
              <thead>
                <tr>
                  <Th>{t('peo.devices.name')}</Th>
                  <Th>{t('peo.devices.lastSeen')}</Th>
                  <Th>{t('peo.devices.state')}</Th>
                  <Th>
                    <span className="sr-only">{t('common.actions')}</span>
                  </Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {live.map((d) => (
                  <tr key={d.id}>
                    <td className="px-3 py-2 text-sm">
                      <span className="font-medium">{d.name}</span>
                      <span className="block text-xs text-slate-600">{[d.platform, d.appVersion].filter(Boolean).join(' ')}</span>
                    </td>
                    <td className="px-3 py-2 text-sm">{d.lastSeenAt ? formatDateTime(d.lastSeenAt) : t('peo.devices.never')}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        <StatusPill tone={d.push ? 'good' : 'neutral'}>{d.push ? t('peo.devices.pushOn') : t('peo.devices.pushOff')}</StatusPill>
                        <StatusPill tone={d.locationConsent ? 'info' : 'neutral'}>{d.locationConsent ? t('peo.devices.locationOn') : t('peo.devices.locationOff')}</StatusPill>
                        <StatusPill tone={d.appLockSet ? 'good' : 'neutral'}>{d.appLockSet ? t('peo.devices.lockOn') : t('peo.devices.lockOff')}</StatusPill>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canManage ? <ConfirmButton label={t('peo.devices.revoke')} confirmLabel={t('peo.devices.revokeConfirm')} pending={revoke.isPending} onConfirm={() => revoke.mutate(d.id)} /> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
          <ErrorLine error={revoke.error} />
        </Card>
      ) : null}
    </div>
  );
}

/** Stand-alone page for the same panel, at /manage/members/:memberId/companion. */
export function CompanionMemberPage() {
  const { memberId = '' } = useParams();
  return (
    <div>
      <PageHeader title={t('peo.member.title')} description={t('peo.member.pageHelp')} />
      <CompanionMemberTab memberId={memberId} />
    </div>
  );
}

/** Group page card (spec 2.4): off by default, what the app may do, and "Invite everyone". */
export function CompanionGroupCard({ scopeId, canEdit }: SettingsCardProps) {
  const { query, save, values } = useScopedSettings(`/groups/${scopeId}/settings`, 'companion');
  const canInvite = useCan('groups.manage_members');
  const toast = useToast();
  const [draft, setDraft] = useState<CompanionGroupSettings | null>(null);
  const [result, setResult] = useState<CompanionInviteResult | null>(null);
  useEffect(() => {
    if (values) setDraft(CompanionGroupSettings.parse(values));
  }, [values]);
  const inviteAll = useMutation({
    mutationFn: () => api.post('/companion/invites', { body: { groupId: scopeId, send: 'email' }, schema: CompanionInviteResult }),
    onSuccess: (r) => {
      setResult(r);
      toast(t('peo.group.invited', { sent: r.sent }));
    },
  });
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  if (!values || !draft) return <Spinner />;
  const changed = JSON.stringify(draft) !== JSON.stringify(CompanionGroupSettings.parse(values));
  const set = (patch: Partial<CompanionGroupSettings>) => setDraft({ ...draft, ...patch });
  return (
    <Card
      title={t('peo.group.title')}
      description={t('peo.group.description')}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!changed || save.isPending} onClick={() => save.mutate(draft)}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <CheckboxField label={t('peo.group.enabled')} hint={t('peo.group.enabledHelp')} checked={draft.enabled} disabled={!canEdit} onChange={(e) => set({ enabled: e.target.checked })} />
        {draft.enabled ? (
          <>
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium text-slate-800">{t('peo.group.permissions')}</legend>
              {PERMISSION_KEYS.map((k) => (
                <CheckboxField key={k} label={t(`peo.perm.${k}`)} hint={t(`peo.perm.${k}.help`)} checked={draft.permissions[k]} disabled={!canEdit} onChange={(e) => set({ permissions: { ...draft.permissions, [k]: e.target.checked } })} />
              ))}
            </fieldset>
            <CheckboxField label={t('peo.group.appLock')} hint={t('peo.group.appLockHelp')} checked={draft.requireAppLock} disabled={!canEdit} onChange={(e) => set({ requireAppLock: e.target.checked })} />
            <CheckboxField label={t('peo.group.selfInvite')} hint={t('peo.group.selfInviteHelp')} checked={draft.selfInviteFromKiosk} disabled={!canEdit} onChange={(e) => set({ selfInviteFromKiosk: e.target.checked })} />
            <div>
              <Button disabled={!canInvite || changed || inviteAll.isPending} onClick={() => inviteAll.mutate()}>
                {t('peo.group.inviteAll')}
              </Button>
              {changed ? <p className="mt-1 text-xs text-slate-600">{t('peo.group.saveFirst')}</p> : null}
              {result ? <p className="mt-2 text-sm text-slate-700">{t('peo.group.inviteResult', { sent: result.sent, skipped: result.skippedNoEmail })}</p> : null}
              <ErrorLine error={inviteAll.error} />
            </div>
          </>
        ) : null}
        <ErrorLine error={save.error} />
      </div>
    </Card>
  );
}
